/**
 * TerrainGewaesser — das Gewässernetz der Region als gedrapte Bänder im
 * Terrain (Flüsse/Kanäle breit, Bäche schmal).
 *
 * Datenquelle ist DASSELBE region-gebackene `gewaesser.json` wie im
 * 2D-WaterwayOverlayRenderer (map-capture/gewaesser-backen.mjs, OSM):
 * 2D und 3D zeigen immer dasselbe Wassernetz. Der Layer ist opt-in —
 * die Megabyte-Datei lädt erst beim ersten Einblenden.
 *
 * Geometrie nach dem Routen-Muster (Alex' Blender-MeshLineToPlane-Doktrin):
 * Höhe wird auf die LINIE projiziert, das Band entsteht danach mit level
 * cross-section — beide Kanten eines Querschnitts liegen auf derselben
 * Höhe, das Band liegt waagrecht im Gelände statt wie eine Textur verzerrt.
 * Da die Kachel-Höhen streamen, wird das Y aller Vertices periodisch
 * nachgeführt, bis es stabil ist (in-place, Grundriss bleibt).
 */
import * as THREE from 'three';
import { SceneNode } from '../core/SceneNode';
import type { TerrainRoot } from './TerrainRoot';

export interface TerrainGewaesserConfig {
    terrain: TerrainRoot;
    /** gewaesser.json der Region ({ linien: [{art, punkte:[[lng,lat],..]}] }) */
    url: string;
    /** Regions-Umriss (gleiches Artefakt wie der Sockel) — Linien werden auf
     *  die Silhouette geclippt. Die gebackene bbox ist ein RECHTECK; ohne
     *  Clip zeichnen Nachbarland-Baeche hinter dem Maskenrand in den Himmel. */
    umrissUrl?: string;
    /** Halbe Bandbreite in Metern für river/canal (Vorgabe 11 m) */
    breiteRiverMeter?: number;
    /** Halbe Bandbreite in Metern für stream (Vorgabe 3.5 m) */
    breiteStreamMeter?: number;
    /** Höhen-Offset über dem Gelände in Metern (gegen Z-Fighting) */
    hoehenOffsetMeter?: number;
    farbe?: string;
}

interface GewaesserLinie {
    art: string;
    punkte: [number, number][];
}

export class TerrainGewaesser extends SceneNode {
    private readonly _cfg: Required<TerrainGewaesserConfig>;
    private readonly _terrain: TerrainRoot;
    private _ladeStand: 'aus' | 'laedt' | 'bereit' = 'aus';
    /** Grundriss je Vertex (Welt-XZ) — fürs in-place Höhen-Nachführen. */
    private _vertexXZ: Float32Array | null = null;
    private _positionen: THREE.BufferAttribute | null = null;
    private _drapeUhr = 0;
    private _letzteSumme = NaN;
    private _versuche = 0;

    constructor(cfg: TerrainGewaesserConfig) {
        super('TerrainGewaesser');
        this._cfg = {
            breiteRiverMeter: 11,
            breiteStreamMeter: 3.5,
            hoehenOffsetMeter: 2,
            farbe: '#5ec8f0',
            umrissUrl: '',
            ...cfg,
        };
        this._terrain = cfg.terrain;
        this._mesh = new THREE.Group();
        this._mesh.name = 'TerrainGewaesser';
        this._mesh.userData.sceneNode = this;
        this._mesh.visible = false;
    }

    protected calculateBoundingBox(): THREE.Box3 {
        const box = new THREE.Box3();
        if (this._mesh) box.setFromObject(this._mesh);
        if (box.isEmpty()) box.setFromCenterAndSize(new THREE.Vector3(), new THREE.Vector3(1, 1, 1));
        return box;
    }

    /** Ein-/ausblenden; erstes Einblenden lädt und baut die Bänder. */
    setSichtbar(sichtbar: boolean): void {
        if (this._mesh) this._mesh.visible = sichtbar;
        if (sichtbar && this._ladeStand === 'aus') {
            /* Fail-safe: _lade faengt Fetch-Fehler selbst, aber ein Wurf im
             * Geometriebau (_baue) darf nie als unhandled rejection enden —
             * der Layer degradiert stattdessen sichtbar im Log auf 'aus'. */
            this._lade().catch((e) => {
                console.error('[terrain] Gewässer-Aufbau fehlgeschlagen:', e);
                this._ladeStand = 'aus';
            });
        }
    }

    get sichtbar(): boolean {
        return this._mesh?.visible ?? false;
    }

    private async _lade(): Promise<void> {
        this._ladeStand = 'laedt';
        const [daten, umriss] = await Promise.all([
            fetch(this._cfg.url).then((r) => r.json()).catch(() => null),
            this._cfg.umrissUrl
                ? fetch(this._cfg.umrissUrl).then((r) => r.json()).catch(() => null)
                : Promise.resolve(null),
        ]);
        const linien: GewaesserLinie[] = daten?.linien;
        if (!linien?.length) {
            console.warn('[terrain] Gewässer nicht ladbar:', this._cfg.url);
            this._ladeStand = 'aus';
            return;
        }
        const ring: [number, number][] | null = umriss?.ringe?.[0]?.length >= 4 ? umriss.ringe[0] : null;
        this._baue(linien, ring);
        this._ladeStand = 'bereit';
    }

    /** Punkt-in-Polygon (Ray-Casting) im lng/lat-Raum des Umriss-Rings. */
    private static _imRing(ring: [number, number][], lng: number, lat: number): boolean {
        let drin = false;
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            const [xi, yi] = ring[i], [xj, yj] = ring[j];
            if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
                drin = !drin;
            }
        }
        return drin;
    }

    /** Bänder bauen: ein gemeinsames, indiziertes Mesh für alle Linien. */
    private _baue(linien: GewaesserLinie[], ring: [number, number][] | null): void {
        const proj = this._terrain.projection;
        const halbR = this._cfg.breiteRiverMeter * proj.unitsPerMeter;
        const halbS = this._cfg.breiteStreamMeter * proj.unitsPerMeter;
        /* Segmente über ~150 m fürs Drapen unterteilen — die Quelldaten sind
         * Douglas-Peucker-vereinfacht, lange Geraden würden Kuppen schneiden. */
        const maxSeg = 150 * proj.unitsPerMeter;

        const pos: number[] = [];
        const xz: number[] = [];
        const idx: number[] = [];

        for (const linie of linien) {
            if (!linie.punkte || linie.punkte.length < 2) continue;
            const halb = linie.art === 'stream' ? halbS : halbR;

            // Auf die Regions-Silhouette clippen: aussenliegende Punkte brechen
            // die Linie in Teilstuecke (Grenzzubringer enden an der Grenze).
            const stuecke: [number, number][][] = [];
            if (ring) {
                let akt: [number, number][] = [];
                for (const p of linie.punkte) {
                    if (TerrainGewaesser._imRing(ring, p[0], p[1])) {
                        akt.push(p);
                    } else if (akt.length) {
                        stuecke.push(akt);
                        akt = [];
                    }
                }
                if (akt.length) stuecke.push(akt);
            } else {
                stuecke.push(linie.punkte);
            }

            for (const stueck of stuecke) {
            if (stueck.length < 2) continue;
            // geo → Welt-XZ, lange Segmente verdichten
            const pts: { x: number; z: number }[] = [];
            for (let i = 0; i < stueck.length; i++) {
                const [lng, lat] = stueck[i];
                const w = proj.geoToWorld(lng, lat, 0);
                if (pts.length > 0) {
                    const a = pts[pts.length - 1];
                    const d = Math.hypot(w.x - a.x, w.z - a.z);
                    const teile = Math.ceil(d / maxSeg);
                    for (let t = 1; t < teile; t++) {
                        pts.push({ x: a.x + ((w.x - a.x) * t) / teile, z: a.z + ((w.z - a.z) * t) / teile });
                    }
                }
                pts.push({ x: w.x, z: w.z });
            }

            // Band: je Punkt zwei Kanten senkrecht zur Laufrichtung (XZ),
            // beide auf gleicher Höhe (level cross-section, Y kommt vom Drape)
            const basis = pos.length / 3;
            for (let i = 0; i < pts.length; i++) {
                const p = pts[i];
                const vor = pts[Math.max(0, i - 1)];
                const nach = pts[Math.min(pts.length - 1, i + 1)];
                let dx = nach.x - vor.x, dz = nach.z - vor.z;
                const len = Math.hypot(dx, dz) || 1;
                dx /= len; dz /= len;
                const nx = -dz, nz = dx;
                pos.push(p.x + nx * halb, 0, p.z + nz * halb);
                pos.push(p.x - nx * halb, 0, p.z - nz * halb);
                xz.push(p.x + nx * halb, p.z + nz * halb, p.x - nx * halb, p.z - nz * halb);
            }
            for (let i = 0; i < pts.length - 1; i++) {
                const a = basis + i * 2;
                idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
            }
            }
        }

        const geo = new THREE.BufferGeometry();
        this._positionen = new THREE.BufferAttribute(new Float32Array(pos), 3);
        this._vertexXZ = new Float32Array(xz);
        geo.setAttribute('position', this._positionen);
        geo.setIndex(idx);
        const material = new THREE.MeshBasicMaterial({
            color: this._cfg.farbe,
            transparent: true,
            opacity: 0.85,
            depthWrite: false,
            polygonOffset: true,
            polygonOffsetFactor: -2,
            polygonOffsetUnits: -2,
            side: THREE.DoubleSide,
        });
        const mesh = new THREE.Mesh(geo, material);
        mesh.name = 'gewaesser/baender';
        mesh.frustumCulled = false;
        (this._mesh as THREE.Group).add(mesh);
        this._drape();
        geo.computeBoundingSphere();
    }

    /** Y aller Vertices aus dem aktuellen Höhenfeld setzen (in-place). */
    private _drape(): number {
        if (!this._positionen || !this._vertexXZ) return 0;
        const proj = this._terrain.projection;
        const offset = this._cfg.hoehenOffsetMeter * proj.unitsPerMeter;
        const arr = this._positionen.array as Float32Array;
        let summe = 0;
        for (let v = 0; v < this._vertexXZ.length / 2; v++) {
            const y = this._terrain.getWorldY(this._vertexXZ[v * 2], this._vertexXZ[v * 2 + 1]);
            if (y !== null) {
                arr[v * 3 + 1] = y + offset;
                summe += y;
            }
        }
        this._positionen.needsUpdate = true;
        return summe;
    }

    /** Höhen den streamenden Kacheln nachführen, bis stabil (Sockel-Muster). */
    protected onUpdate(deltaTime: number): void {
        if (this._ladeStand !== 'bereit' || !this._mesh?.visible || this._versuche > 12) return;
        this._drapeUhr += deltaTime;
        if (this._drapeUhr < 2) return;
        this._drapeUhr = 0;
        const summe = this._drape();
        if (Math.abs(summe - this._letzteSumme) < 0.01) return;
        this._letzteSumme = summe;
        this._versuche++;
    }

    /** Nach Height-Revisionen von außen (refreshSurface-Pfad). */
    refreshSurface(): void {
        this._versuche = 0;
        this._letzteSumme = NaN;
    }
}
